import React, { createRef, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { FlowChatLauncher } from '@coopercarga/flow-chat';
import { validatePermission } from 'actions/index';
import { UserContext } from 'contexts/UserContext';
import { isAuthenticated } from 'services/auth';
import theme from 'v3/theme';
import { mustChangePassword } from 'v3/utils/password';
import { getFlowChatConfig } from './config';
import { applyLauncherStyle } from './launcherStyle';
import { matchScreen } from './screenHints';
import './flowChat.css';

// Botão do assistente Flow Chat. Montado uma vez em src/routes.js, ao lado de <Routes>: dentro
// do roteador e do UserContext, mas fora das páginas, para o iframe não ser recriado a cada
// navegação. Regras em docs/integrations/flow-chat.md.

export const FLOW_CHAT_PERMISSION = 'VISUALIZAR_ASSISTENTE_FLOW';

/** Handle do launcher (open, minimize, signOutAndUnmount, updateScreenHint, getStatus). */
export const flowChatRef = createRef();

const config = getFlowChatConfig();

// O pacote usa o label no título do painel e no nome acessível do botão e do painel.
const LABEL = 'Flow Chat';

// O portal só tem tema claro: `auto` deixaria o painel escuro num sistema com tema escuro.
const THEME = { mode: 'light', tokens: { accent: theme.colors.primary } };

// Mesmas condições dos guards de rota privada, mais a permissão do assistente. Não depende de
// isLoading, que volta a true a cada troca de empresa e recriaria o iframe.
function canUseFlowChat(user) {
  const authenticated = isAuthenticated();
  return (
    user.isUserActive &&
    !user.isUserBlocked &&
    authenticated &&
    !mustChangePassword({ ...user, authenticated }) &&
    validatePermission(FLOW_CHAT_PERMISSION)
  );
}

/**
 * `routes`: rotas privadas do menu com a dica de cada tela (`toScreenRoutes`); fora delas (login,
 * públicas) não há botão.
 */
export function FlowChatHost({ routes }) {
  const { pathname } = useLocation();
  const user = useContext(UserContext);
  const screen = useMemo(() => matchScreen(routes, pathname), [routes, pathname]);
  const enabled = Boolean(config.enabled && screen && canUseFlowChat(user));

  // O launcher monta no efeito dele, que roda antes deste; cada `enabled` novo é um botão novo.
  useEffect(() => {
    if (enabled) applyLauncherStyle(LABEL);
  }, [enabled]);

  return (
    <FlowChatLauncher
      ref={flowChatRef}
      enabled={enabled}
      flowUrl={config.flowUrl}
      allowCustomFlowOrigin={config.allowCustomFlowOrigin}
      position="right"
      label={LABEL}
      theme={THEME}
      screenHint={screen ? screen.hint : null}
    />
  );
}
